import React, { useEffect, useState, useCallback } from 'react';
import Helmet from 'react-helmet';
import PageHeader from 'retired/PageHeader';
import { useHistory } from 'react-router-dom';
import { Alert, Typography, LinearProgress } from '@mui/material';
import {
  ExternalMatchRequestRule,
  useGetExternalMatchRequestRulesQuery,
  useSetExternalMatchRequestRulesMutation,
} from 'generated/graphql';

import RuleListItem from './RuleListItem';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'entities/user/selectors';
import { Privileges } from 'utils/privileges';
import { showErrorAction } from 'containers/Notification/actions';
import DeleteRuleModal from './DeleteRuleModal';
import { convertRulesToInput } from '../helpers';
import RuleDescription from './RuleDescription';
import EmptyState from './EmptyState';
import { useRulesContext } from '../RulesContext';
import { getUserFriendlyErrorMessage } from '../getUserFriendlyErrorMessage';

const ExternalMatchRequestRules: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const { rules: externalMatchRequestRules, setRules: setExternalMatchRequestRules } =
    useRulesContext();

  const [clientExternalMatchRequestRules, setClientExternalMatchRequestRules] = useState<
    ExternalMatchRequestRule[]
  >([]);

  const [ruleToDelete, setRuleToDelete] = useState<ExternalMatchRequestRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // Separate loading state for delete operations
  const [isDeletingRule, setIsDeletingRule] = useState(false);

  const {
    data,
    loading: externalRulesLoading,
    refetch,
  } = useGetExternalMatchRequestRulesQuery({
    variables: {},
    fetchPolicy: 'network-only',
    skip: !user?.hasPrivilege(Privileges.EXTERNAL_MATCH_REQUEST_RULES_MANAGE),
  });

  const [setExternalMatchRequestRulesMutation] = useSetExternalMatchRequestRulesMutation();

  useEffect(() => {
    if (data?.externalMatchRequestRules) {
      setExternalMatchRequestRules(data.externalMatchRequestRules);
      setClientExternalMatchRequestRules(data.externalMatchRequestRules);
    }
  }, [data]);

  const updateRules = useCallback(
    async ({
      newRules,
      operationType,
    }: {
      newRules: ExternalMatchRequestRule[];
      operationType: 'delete' | 'reorder';
    }) => {
      try {
        // Set appropriate loading state based on operation type
        if (operationType === 'delete') setIsDeletingRule(true);

        const rulesInput = convertRulesToInput(newRules);
        const result = await setExternalMatchRequestRulesMutation({
          variables: { rules: rulesInput },
        });
        // On success, update the backend-confirmed state with the actual response
        const savedRules = result.data?.setExternalMatchRequestRules;

        if (savedRules) {
          setExternalMatchRequestRules(savedRules);
        }
        return true;
      } catch (error) {
        const userFriendlyErrorMessage = getUserFriendlyErrorMessage(error);

        dispatch(showErrorAction(userFriendlyErrorMessage));

        // Revert client state to the last known good backend state
        setClientExternalMatchRequestRules(externalMatchRequestRules);

        // Refetch the latest rules from the backend to ensure we have the correct state
        try {
          const { data: refetchedData } = await refetch();
          if (refetchedData?.externalMatchRequestRules) {
            setExternalMatchRequestRules(refetchedData.externalMatchRequestRules);
            setClientExternalMatchRequestRules(refetchedData.externalMatchRequestRules);
          }
        } catch (refetchError) {
          const refetchErrorMessage = getUserFriendlyErrorMessage(refetchError);
          dispatch(
            showErrorAction(`Failed to refetch rules after update error. ${refetchErrorMessage}`)
          );
        }

        return false;
      } finally {
        if (operationType === 'delete') setIsDeletingRule(false);
      }
    },
    [setExternalMatchRequestRulesMutation, dispatch, refetch, externalMatchRequestRules]
  );

  // #region Deletion Logic
  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;

    const updatedRules = clientExternalMatchRequestRules.filter(
      (rule) => rule.id !== ruleToDelete.id
    );
    const success = await updateRules({
      newRules: updatedRules,
      operationType: 'delete',
    });

    if (success) {
      setClientExternalMatchRequestRules(updatedRules);
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setRuleToDelete(null);
  };
  // #endregion

  // #region Drag & drop logic
  const moveRule = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragRule = clientExternalMatchRequestRules[dragIndex];
      const newRules = [...clientExternalMatchRequestRules];
      newRules.splice(dragIndex, 1);
      newRules.splice(hoverIndex, 0, dragRule);
      setClientExternalMatchRequestRules(newRules);
    },
    [clientExternalMatchRequestRules]
  );

  const saveRuleOrder = useCallback(
    async (newRules: ExternalMatchRequestRule[]) => {
      await updateRules({
        newRules,
        operationType: 'reorder',
      });
    },
    [updateRules]
  );
  // #endregion

  return (
    <>
      <Helmet title={'External Donation Auto Response Rules'} />
      <PageHeader
        title="Rules"
        subtitle="Configure automated approval and denial rules for external donation match requests"
        onArrowClick={() => history.push('/donations')}
        buttonText="Add Rules"
        onButtonClick={() => history.push('/donations/external-match-request-rules/new')}
      />
      {clientExternalMatchRequestRules?.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: '16px' }}>
          <Typography fontWeight="500">Collision Handling Active</Typography>
          <Typography>
            When multiple rules match a request, the highest priority rule (lowest number on the
            left) will be applied. Eg the #1 of the list below will rule above all.
          </Typography>
        </Alert>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {externalRulesLoading ? (
          <LinearProgress />
        ) : clientExternalMatchRequestRules?.length > 0 ? (
          clientExternalMatchRequestRules.map((rule, index) => (
            <RuleListItem
              key={rule.id}
              index={index}
              moveRule={moveRule}
              onDragEnd={() => saveRuleOrder(clientExternalMatchRequestRules)}
              onRemove={() => {
                setRuleToDelete(rule);
                setDeleteDialogOpen(true);
              }}
              onEdit={() => history.push(`/donations/external-match-request-rules/${rule.id}`)}
              {...rule}
            />
          ))
        ) : (
          <EmptyState />
        )}
      </div>

      <DeleteRuleModal
        deleteDialogOpen={deleteDialogOpen}
        handleCancelDelete={handleCancelDelete}
        ruleToDelete={ruleToDelete}
        deletingRule={isDeletingRule}
        handleDeleteRule={handleDeleteRule}
      >
        {ruleToDelete && (
          <RuleDescription
            confidenceScore={ruleToDelete?.confidenceScore}
            minAmount={ruleToDelete?.minAmount}
            maxAmount={ruleToDelete?.maxAmount}
            type={ruleToDelete?.type}
            style={{ fontWeight: '500' }}
          />
        )}
      </DeleteRuleModal>
    </>
  );
};

export default ExternalMatchRequestRules;
